// src/services/filesService.ts

interface DownloadConfig {
  apiUrl: string;
  token: string;
}

function base64ToBlob(base64Data: string, contentType: string): Blob
{
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const byteArrays: Uint8Array[] = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize)
  {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const bytes = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) bytes[i] = slice.charCodeAt(i);
    byteArrays.push(bytes);
  }
  return new Blob(byteArrays as BlobPart[], { type: contentType || 'application/octet-stream' });
}

/**
 * Downloads a file via the backend API (api/Attachment/GetFile or api/EmbebbedImages/GetFile).
 * The endpoint returns the file encoded as a base64 JSON string with x-filename and content-type headers.
 * Silently does nothing if the API call fails or attachmentId is invalid.
 */
export function downloadAttachment(
  attachmentId: number,
  embeddedImageId: number | null,
  filename: string,
  _fileUrl: string,
  cfg: DownloadConfig,
): void
{
  let urlApi = '';
  if (attachmentId > 0)
    urlApi = `api/attachment/GetFile?attachmentId=${attachmentId}`;
  else if (embeddedImageId && embeddedImageId > 0)
    urlApi = `api/EmbebbedImages/GetFile?attachmentId=${embeddedImageId}`;

  if (!urlApi) return;

  const baseUrl = cfg.apiUrl.replace(/\/$/, '');
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=UTF-8',
  };
  if (cfg.token) headers['Authorization'] = `Bearer ${cfg.token}`;

  fetch(`${baseUrl}/${urlApi}`, { headers })
    .then(res =>
    {
      const serverFilename = res.headers.get('x-filename');
      const contentType = res.headers.get('content-type') || 'application/octet-stream';
      const downloadName = (serverFilename || filename).replace(/(\r\n|\n|\r)/gm, '');
      return res.text().then(data => ({ data, contentType, downloadName }));
    })
    .then(({ data, contentType, downloadName }) =>
    {
      const blob = base64ToBlob(JSON.parse(data), contentType);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    })
    .catch(() => {});
}
